import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { And, Automation, ChoicesInterface, RuleInterface, StringEquals } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { AutomationReplacementChipsComponent } from '../../../../../common/components';
import { PackageSelectorService } from '../../../../../data-services/packages-selector-service';
import { CommonTriggerPanelComponent } from '../../../common/common-trigger-panel.component';
import { ANY_PACKAGE_REGEX, NO_SELECTION } from '../constants';
import { InterestOrigin } from '../enum';
import { InterestOriginSelectorComponent } from '../interest-origin-selector/interest-origin-selector.component';
import { PackageInterestService } from '../package-interest.service';

@Component({
  selector: 'automata-automation-package-interest-panel',
  templateUrl: './package-interest-panel.component.html',
  styleUrls: ['./package-interest-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyPipesModule,
    InterestOriginSelectorComponent,
    AutomationReplacementChipsComponent,
  ],
})
export class PackageInterestPanelComponent
  extends CommonTriggerPanelComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  service = inject(PackageSelectorService);
  control: UntypedFormControl = new UntypedFormControl();

  @ViewChild(InterestOriginSelectorComponent) private readonly interestOriginComponent: InterestOriginSelectorComponent;
  automation: Automation;
  taskDefinitionId: string;
  triggerChoices: ChoicesInterface;

  private readonly interestOriginSelection$$ = new BehaviorSubject<string>(InterestOrigin.INTEREST_ORIGIN_VBC_USER);

  /** Completes when the panel is destroyed. */
  private destroyed$$ = new Subject<void>();

  constructor(protected readonly automationService: AutomationViewService) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setPackageSelection();
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }

  ngAfterViewInit(): void {
    // Setup the panel.
    this.setOriginSelection();
  }

  private setOriginSelection(): void {
    const cls = PackageInterestService;
    const origin = cls.getOriginSelectionFromChoices(this.triggerChoices);
    this.interestOriginComponent.setOrigin(origin);
  }

  private setPackageSelection(): void {
    this.automationService
      .isBuiltInAutomationView()
      .pipe(
        take(1),
        map((isBuiltInAutomationView) => {
          const cls = PackageInterestService;
          let packageId = cls.getPackageIdFromChoices(this.triggerChoices);
          if (isBuiltInAutomationView && packageId === NO_SELECTION) {
            packageId = ANY_PACKAGE_REGEX;
          }
          this.control.setValue(packageId);
        }),
      )
      .subscribe();
  }

  getBaseRules(): RuleInterface[] {
    return this.getRules();
  }

  private getRules(): RuleInterface[] {
    const next =
      this.automation.triggerStep?.choices?.rules?.length > 0
        ? this.automation.triggerStep.choices.rules[0].next
        : NO_SELECTION;

    const packageIdValue = this.control.value;

    const andRule = new And(
      [
        new StringEquals('{.identifier_type}', this.interestOriginSelection$$.getValue()),
        new StringEquals('{.package_id}', packageIdValue),
      ],
      next,
    );

    return [andRule.toRuleInterface()];
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return this.interestOriginComponent.getControlsToValidateOnSave().concat(this.control);
  }

  isFormValid(): boolean {
    const isOriginFormValid = this.interestOriginComponent.isFormValid();
    const isPackageSelectorFormValid = this.control.valid;

    return isOriginFormValid && isPackageSelectorFormValid;
  }

  fillRuleValues(_rules: RuleInterface[]): RuleInterface[] {
    return this.getRules();
  }

  handleInterestOriginSelection(origin: string): void {
    this.interestOriginSelection$$.next(origin);
  }

  isTemplate(): boolean {
    return this.automation ? this.automation.isTemplate : false;
  }

  finishedLoading(): void {
    this.savingEnabled$.next(true);
  }
}
